import { HeaderH2 } from '@/components/HeaderH2';
import {
  useUpdateAiServiceEnablement,
  useUpdateModelTrainingSetting,
} from '@/context/services/ai_service_setting/AiServiceSetting.service';
import type { AiService } from '@/context/services/ai_service_setting/type';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, useCallback } from 'react';
import { ModelTrainingSetting } from '../ModelTrainingSetting/ModelTrainingSetting';
import { AiAgreementLink } from '../aiAgreementLink';
import { AiServiceItem } from './AiServiceItem/AiServiceItem';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);
const TOAST_DURATION = 5;

type AiServiceSettingsProps = {
  isModelTrainingAllowed: boolean;
  services: AiService[];
};

export const AiServiceSettings: FC<AiServiceSettingsProps> = ({
  isModelTrainingAllowed,
  services,
}) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { send } = useErrorTrackingSystem();
  const {
    isPending: isModelTrainingUpdatePending,
    mutateAsync: updateModelTrainingSetting,
  } = useUpdateModelTrainingSetting();
  const {
    isPending: isAiServiceEnablementUpdatePending,
    mutateAsync: updateAiServiceEnablement,
  } = useUpdateAiServiceEnablement();

  const handleModelTrainingAllowedChange = useCallback(
    async (isAllowed: boolean) => {
      try {
        const result = await updateModelTrainingSetting(isAllowed);

        if (result.isSuccessful) {
          notify.success({
            duration: TOAST_DURATION,
            title: t(
              isAllowed
                ? 'ai_service_setting.model_training.allowed_success'
                : 'ai_service_setting.model_training.disallowed_success'
            ),
          });
          return;
        }

        notify.error({ duration: TOAST_DURATION, title: result.message });
      } catch (error) {
        send(error instanceof Error ? error : String(error));
        notify.error({
          duration: TOAST_DURATION,
          title: t('ai_service_setting.model_training.update_error'),
        });
      }
    },
    [notify, send, t, updateModelTrainingSetting]
  );

  const handleAiServiceEnablementChange = useCallback(
    async (service: AiService, isEnabled: boolean) => {
      try {
        const result = await updateAiServiceEnablement(service, isEnabled);

        if (result.isSuccessful) {
          notify.success({
            duration: TOAST_DURATION,
            title: t(
              isEnabled
                ? 'ai_service_setting.service_start_success'
                : 'ai_service_setting.service_stop_success',
              { serviceName: service.name }
            ),
          });
          return;
        }

        notify.error({ duration: TOAST_DURATION, title: result.message });
      } catch (error) {
        send(error instanceof Error ? error : String(error));
        notify.error({
          duration: TOAST_DURATION,
          title: t(
            isEnabled
              ? 'ai_service_setting.service_start_error'
              : 'ai_service_setting.service_stop_error',
            { serviceName: service.name }
          ),
        });
      }
    },
    [notify, send, t, updateAiServiceEnablement]
  );

  return (
    <div className={cx('container')} data-testid='agreed-setting'>
      <HeaderH2>{t('ai_service_setting.settings_heading')}</HeaderH2>

      <section className={cx('services')}>
        {services.map((service) => (
          <AiServiceItem
            key={service.service_type}
            isUpdatePending={isAiServiceEnablementUpdatePending}
            onEnablementChange={handleAiServiceEnablementChange}
            service={service}
          />
        ))}
      </section>

      <ModelTrainingSetting
        isAllowed={isModelTrainingAllowed}
        isInitial={false}
        isUpdatePending={isModelTrainingUpdatePending}
        onChange={handleModelTrainingAllowedChange}
      />

      <AiAgreementLink />
    </div>
  );
};
